import { Link } from 'react-router-dom';
import { MapPin, Bed, Sofa, Eye, Heart, IndianRupee, GitCompare, ShieldCheck } from 'lucide-react';
import { useCompare } from '../context/CompareContext';
import ShareButtons from './ShareButtons';

const LISTING_BADGES = {
  featured: { label: 'Featured', cls: 'bg-amber-500 text-white' },
  premium: { label: 'Premium', cls: 'bg-purple-600 text-white' },
  basic: { label: 'Boosted', cls: 'bg-blue-600 text-white' },
  free: null
};

const STATUS_COLORS = {
  live: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  rejected: 'bg-red-100 text-red-700'
};

const LISTER_TAG = {
  owner:  { label: 'Owner', cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  broker: { label: 'Broker', cls: 'bg-orange-100 text-orange-700 border border-orange-200' },
};

export default function PropertyCard({ property, showStatus = false, isSaved = false, onToggleSave }) {
  const {
    property_id, title, city, area, bhk_type, furnishing,
    rent, images, listing_type, status, views, property_type, property_category, owner_verified, owner_role,
    carpet_area
  } = property;

  const { toggle, isInList, isFull } = useCompare();
  const inCompare = isInList(property_id);

  const badge = LISTING_BADGES[listing_type];
  const imgSrc = images?.[0];
  const displayType = bhk_type ? `${bhk_type} ${property_type}` : property_type;

  const handleSaveClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleSave) onToggleSave(property_id);
  };

  const handleCompareClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(property);
  };

  return (
    <Link
      to={`/property/${property_id.replace(/_/g, '-')}`}
      className="group bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.04)] border border-slate-100 overflow-hidden hover:shadow-[0_8px_30px_rgb(0,0,0,0.10)] hover:-translate-y-1 transition-all duration-300 block"
      data-testid={`property-card-${property_id}`}
    >
      {/* Image */}
      <div className="relative h-52 w-full overflow-hidden bg-slate-100">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full img-placeholder flex items-center justify-center">
            <span className="text-slate-300 text-4xl">🏠</span>
          </div>
        )}
        {/* Listing Badge */}
        {badge && (
          <span className={`absolute top-3 left-3 text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide ${badge.cls}`} data-testid="listing-badge">
            {badge.label}
          </span>
        )}
        {/* Save / Status Button */}
        {showStatus ? (
          <span className={`absolute top-3 right-3 text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_COLORS[status] || 'bg-slate-100 text-slate-600'}`}>
            {status}
          </span>
        ) : onToggleSave ? (
          <button
            onClick={handleSaveClick}
            className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all shadow-sm ${
              isSaved ? 'bg-red-500 text-white' : 'bg-white/90 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100'
            }`}
            data-testid={`save-btn-${property_id}`}
            title={isSaved ? 'Remove from saved' : 'Save property'}
          >
            <Heart className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
          </button>
        ) : null}
        {/* Views */}
        {views > 0 && !showStatus && (
          <span className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
            <Eye className="w-3 h-3" />{views}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h3 className="font-semibold text-slate-900 text-base line-clamp-1 group-hover:text-blue-700 transition-colors" data-testid="property-title">
            {title || displayType}
          </h3>
          <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
            {owner_role && LISTER_TAG[owner_role] && (
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${LISTER_TAG[owner_role].cls}`} data-testid="lister-tag">
                {LISTER_TAG[owner_role].label}
              </span>
            )}
            {owner_verified && (
              <ShieldCheck className="w-4 h-4 text-blue-600" title="Verified Owner" data-testid="verified-badge" />
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-slate-500 text-sm mb-3">
          <MapPin className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
          <span className="truncate">{area}{city && area ? ', ' : ''}{city}</span>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {bhk_type && (
            <span className="flex items-center gap-1 text-xs text-slate-600 bg-slate-50 px-2 py-1 rounded-lg">
              <Bed className="w-3 h-3" />{bhk_type}
            </span>
          )}
          {carpet_area && (
            <span className="text-xs text-slate-600 bg-slate-50 px-2 py-1 rounded-lg">
              {carpet_area} sq.ft.
            </span>
          )}
          {furnishing && furnishing !== 'unfurnished' && property_category !== 'industrial' && (
            <span className="flex items-center gap-1 text-xs text-slate-600 bg-slate-50 px-2 py-1 rounded-lg">
              <Sofa className="w-3 h-3" />{furnishing}
            </span>
          )}
          {property_category && (
            <span className="text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded-lg capitalize">{property_category}</span>
          )}
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <div className="flex items-center gap-0.5">
            <IndianRupee className="w-4 h-4 text-blue-700 font-bold" />
            <span className="text-lg font-bold text-blue-700" data-testid="property-rent">
              {Number(rent).toLocaleString('en-IN')}
            </span>
            <span className="text-slate-400 text-xs ml-1">/mo</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
              <ShareButtons
                compact
                url={`${window.location.origin}/property/${property_id.replace(/_/g, '-')}`}
                title={title}
                rent={rent}
                city={city}
              />
            </div>
            <button
              onClick={handleCompareClick}
              disabled={!inCompare && isFull}
              className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg transition-colors ${
                inCompare ? 'bg-blue-700 text-white'
                : isFull ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-700'
              }`}
              data-testid={`compare-btn-${property_id}`}
              title={isFull && !inCompare ? 'Max 3 properties' : 'Compare'}
            >
              <GitCompare className="w-3 h-3" />
              {inCompare ? 'Added' : 'Compare'}
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}
